// src/redux/groupSlice.js

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// Acción asíncrona para obtener datos de un grupo
export const fetchGroupData = createAsyncThunk(
  'groups/fetchGroupData',
  async ({ unique_key, database, procedure, fechaInicio, fechaFin, perPage }, { rejectWithValue }) => {
    console.log(`[fetchGroupData] Iniciando para unique_key: ${unique_key}`);

    try {
      const response = await fetch(`${autocrm_globals.api_url}get_group_data`, {
        method: 'POST',
        headers: {
          'X-WP-Nonce': autocrm_globals.nonce,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          unique_key, 
          database, 
          procedure, 
          parameters: { 
            fechaInicio, 
            fechaFin, 
            perPage: parseInt(perPage, 10),
            page: 1,                   // Número de página (actualizado)
            p_column_filter: '',       // Filtro por columna (nuevo)
            p_field_filter: ''         // Filtro por campo
          } 
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        console.error(`[fetchGroupData] Error: ${data.message || response.statusText}`);
        return rejectWithValue(data.message || 'Error al obtener datos del grupo.');
      }

      console.log(`[fetchGroupData] Datos recibidos para unique_key: ${unique_key}`, data);

      return { unique_key, data };
    } catch (error) {
      console.error(`[fetchGroupData] Error: ${error.message}`);
      return rejectWithValue(error.message);
    }
  }
);

// Acción asíncrona para actualizar el estado de un pedido
export const updateOrderStatus = createAsyncThunk(
  'groups/updateOrderStatus',
  async ({ orderId, newStatus, unique_key }, { rejectWithValue }) => {
    console.log(`[updateOrderStatus] Actualizando orderId: ${orderId} a: ${newStatus}`);
    
    try {
      const response = await fetch(`${autocrm_globals.api_url}update_order_status`, {
        method: 'POST',
        headers: {
          'X-WP-Nonce': autocrm_globals.nonce,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ orderId, newStatus }),
      });

      const data = await response.json();

      if (!response.ok) {
        console.error(`[updateOrderStatus] Error: ${data.message || response.statusText}`);
        return rejectWithValue({ orderId, message: data.message || 'Error al actualizar estado del pedido.' });
      }

      console.log(`[updateOrderStatus] Datos recibidos para orderId: ${orderId}`, data);

      return { unique_key, orderId, newStatus };
    } catch (error) {
      console.error(`[updateOrderStatus] Error: ${error.message}`);
      return rejectWithValue({ orderId, message: error.message });
    }
  }
);

const groupSlice = createSlice({
  name: 'groups',
  initialState: {
    data: {}, // Datos de los grupos por unique_key
    errors: {}, // Errores al obtener datos de grupos
    updatingOrderStatus: {}, // Estado de actualización de pedidos
    updateErrors: {}, // Errores al actualizar pedidos
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Manejo de fetchGroupData
      .addCase(fetchGroupData.fulfilled, (state, action) => {
        const { unique_key, data } = action.payload;
        console.log(`[groupSlice] fetchGroupData.fulfilled para unique_key: ${unique_key}`, data);
        state.data[unique_key] = data;
        state.errors[unique_key] = null;
      })
      .addCase(fetchGroupData.rejected, (state, action) => {
        const { unique_key } = action.meta.arg;
        console.error(`[groupSlice] fetchGroupData.rejected para unique_key: ${unique_key}`, action.payload);
        state.data[unique_key] = [];
        state.errors[unique_key] = action.payload;
      })

      // Manejo de updateOrderStatus
      .addCase(updateOrderStatus.pending, (state, action) => {
        const { orderId } = action.meta.arg;
        console.log(`[groupSlice] updateOrderStatus.pending para orderId: ${orderId}`);
        state.updatingOrderStatus[orderId] = true;
        state.updateErrors[orderId] = null;
      })
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        const { unique_key, orderId, newStatus } = action.payload;
        console.log(`[groupSlice] updateOrderStatus.fulfilled para orderId: ${orderId}`);

        if (state.data[unique_key]) {
          const rowIndex = state.data[unique_key].findIndex(row => row.order_id === orderId);
          if (rowIndex !== -1) {
            state.data[unique_key][rowIndex].order_status = newStatus;
            console.log(`[groupSlice] Estado del pedido actualizado para orderId: ${orderId}`);
          } else {
            console.warn(`[groupSlice] No se encontró order_id: ${orderId} en los datos del grupo: ${unique_key}`);
          }
        }

        state.updatingOrderStatus[orderId] = false;
        state.updateErrors[orderId] = null;
      })
      .addCase(updateOrderStatus.rejected, (state, action) => {
        const { orderId } = action.meta.arg;
        console.error(`[groupSlice] updateOrderStatus.rejected para orderId: ${orderId}`, action.payload);
        state.updatingOrderStatus[orderId] = false;
        state.updateErrors[orderId] = action.payload.message || action.payload;
      });
  },
});

export default groupSlice.reducer;
